import * as React from 'react';
import { alpha, InputAdornment, OutlinedInputProps, TextField, Theme } from '@mui/material';
import styled, { css } from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { generateValidations, MaterialUiFieldProps } from '../../core/form';
import { formatNumberWithComma } from '../../lib/utils';
import {
	borderGrey,
	brandingColourHeavy,
	errorRed,
	labelGrey,
	whiteBackground
} from '../../appComponents/cssConstants';
import { DisplayNoneOnPrint } from '../PrintStyles';
import { hasValue } from '../../lib/lib';

const Input = (props: Props) => {
	const {
		label,
		type,
		name,
		startAdornment,
		endAdornment,
		fullWidth = false,
		displayInLineLabel = true,
		readOnly = false,
		isWholeNumber = false,
		isDecimalNumber = false,
		autoFocus = false,
		hasBottomMargin = true,
		onPaste
	} = props;
	const {
		setValue,
		formState: { errors }
	} = useFormContext();
	const { field } = useController({ name, rules: generateValidations(props) });
	const inputRef = React.useRef<HTMLInputElement>(null);

	const maxLength = props.maxLength ?? (props.multiline ? 10000 : 255); // 10000, may need to adjust if not enough.
	const minRows = props.minRows ?? (props.multiline ? 3 : undefined);

	let { value } = field;
	if (value && !Number.isNaN(Number(value)) && (isDecimalNumber || isWholeNumber)) {
		value = formatNumberWithComma(value);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleValueChange = (newValue: any) => {
		let finalValue = newValue;
		if (finalValue && isDecimalNumber) finalValue = finalValue.replace(/[^0-9.]/g, '');
		else if (finalValue && isWholeNumber) finalValue = finalValue.replace(/[^0-9]/g, '');

		setValue(name, hasValue(finalValue) ? finalValue : null, { shouldDirty: true }); // This set empty string to null to fix 400 error for number field which is Int in BE
	};

	const handleFocus = () => {
		const input = inputRef.current;
		if (input && input.type === 'text') {
			// type is readonly property, so can't change it
			// setSelectionRange doesn't work for other type eg email
			try {
				// Delay cursor placement until iOS finishes handling native focus
				requestAnimationFrame(() => {
					try {
						const { length } = input.value;
						input.setSelectionRange(length, length); // show cursor in the end when tap on input for iPhone
					} catch {
						/* ignore */
					}
				});
			} catch {
				// Intentionally left empty to handle cases where setSelectionRange fails
			}
		}
	};

	return (
		<PrintableInputWrapper $fullWidth={fullWidth} $displayInLineLabel={displayInLineLabel}>
			<StyledInput
				ref={field.ref}
				inputRef={inputRef}
				name={name}
				value={value ?? ''}
				type={type ?? 'text'}
				label={label}
				variant="filled"
				size="small"
				error={hasValue(errors[name])}
				helperText={errors[name]?.message as unknown}
				disabled={props.disabled}
				className={props.className}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.target.value)}
				multiline={props.multiline}
				minRows={minRows}
				autoFocus={autoFocus}
				// maxRows={props.maxRows} // Don't set maxRows so that the input area height will be based on the user input value,
				// so no scroll bar will show, so that when printing, no context will be hided.
				placeholder={props.placeholder}
				$displayInLineLabel={displayInLineLabel}
				$hasBottomMargin={hasBottomMargin}
				onBlur={() => {
					if (typeof field.value === 'string') {
						setValue(name, field.value.replace(/ +/g, ' ').trim());
					}
				}}
				onFocus={handleFocus}
				onPaste={onPaste}
				InputProps={
					{
						disableUnderline: true,
						readOnly,
						startAdornment: startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>,
						endAdornment: endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>
					} as Partial<OutlinedInputProps>
				}
				// eslint-disable-next-line react/jsx-no-duplicate-props
				inputProps={{ maxLength }} // This will apply to actual html input element.
				// Only need maxLength here. Other validation attributes will be used in generateValidations for custom validtion only.
			/>
		</PrintableInputWrapper>
	);
};

export default Input;

export const PrintableInputWrapper = styled.div<{ $fullWidth: boolean; $displayInLineLabel: boolean }>`
	display: inline-flex;
	width: 100%;
	vertical-align: ${(props) => (props.$displayInLineLabel ? 'top' : 'bottom')};

	${(props) =>
		props.$fullWidth &&
		css`
			width: 100% !important;
		`}
`;

export const StyledInput = styled(TextField)<{ theme: Theme; $displayInLineLabel: boolean; $hasBottomMargin: boolean }>`
	width: 100%;
	font-size: 16px;
	margin-bottom: ${(props) => (props.$hasBottomMargin ? '10px' : '0')};

	${DisplayNoneOnPrint}

	.MuiInputBase-root {
		border: 1px solid ${borderGrey};
		border-radius: 12px;
		background-color: ${whiteBackground};
		color: black;
		font-size: 16px;
		line-height: 21px;
		transition: ${(props) => props.theme.transitions.create(['border-color', 'background-color', 'box-shadow'])};

		@media (hover: hover) and (pointer: fine) {
			// exclude for mobile devices which caused border color issue in iPhone
			&:hover {
				border-color: ${labelGrey};
			}
		}

		&.Mui-focused {
			border-color: ${brandingColourHeavy};
			border-width: 1px;
			/* box-shadow: ${alpha(brandingColourHeavy, 0.25)} 0 0 0 2px; */
		}

		&.Mui-error {
			border-color: ${errorRed};
		}

		.MuiIconButton-root {
			color: ${borderGrey};
		}
	}

	.MuiInputBase-multiline {
		${(props) =>
			!props.$displayInLineLabel &&
			css`
				padding-top: 12px !important;
				padding-bottom: 13px !important;
			`}
	}

	.MuiInputLabel-root {
		color: ${labelGrey};
		white-space: normal;

		&.Mui-focused {
			color: ${brandingColourHeavy};
		}

		&.Mui-error {
			color: ${errorRed};
		}

		${(props) =>
			!props.$displayInLineLabel &&
			css`
				position: relative;
				max-width: 100%;
				transform: none;
				white-space: initial;
				font-size: 18px;
				line-height: 21px;
				margin: 6px 0;
			`}
	}

	.MuiInputBase-input {
		${(props) =>
			!props.$displayInLineLabel &&
			css`
				padding-top: 12px !important;
				padding-bottom: 13px !important;
			`}
	}

	.MuiInputBase-inputMultiline {
		${(props) =>
			!props.$displayInLineLabel &&
			css`
				padding-top: 0px !important;
				padding-bottom: 0px !important;
			`}
	}

	.MuiFormHelperText-root {
		&.Mui-error {
			font-size: 10px;
			font-weight: 500;
			margin: 5px 0 0 0;
			border-color: ${errorRed};
			color: ${errorRed};
		}
	}

	input:-webkit-autofill {
		-webkit-box-shadow: 0 0 0 30px white inset;
		box-shadow: 0 0 0 30px white inset;
		border-radius: 12px;
	}
`;

interface Props extends MaterialUiFieldProps {
	fullWidth?: boolean;
	isWholeNumber?: boolean;
	isDecimalNumber?: boolean;
	hasBottomMargin?: boolean;
	onPaste?: (e: React.ClipboardEvent) => void;
}
